/**
 * EngineDetailCompetitor Component
 *
 * Engine-specific detail view for Competitor Discovery Engine.
 * Displays discovered competitors in a grid layout.
 */

import { useState } from "react";
import type { CompetitorDiscoveryEngineSpecificAnalysis } from "@a7y/types";
import { Subheading, Text } from "@a7y/tailwind-catalyst";
import { CompetitorAnalysisView } from "@/components/audit/competitor/CompetitorAnalysisView";
import { CollapsibleSection } from "./CollapsibleSection";
import { DevCardBadge } from "./DevCardBadge";

export interface EngineDetailCompetitorProps {
  /** Competitor-specific analysis data */
  analysis: CompetitorDiscoveryEngineSpecificAnalysis;
  /** When true, force-expands all collapsible sections (for print/PDF) */
  printMode?: boolean;
  /** Additional CSS classes */
  className?: string;
}

export function EngineDetailCompetitor({ analysis, printMode, className = "" }: EngineDetailCompetitorProps) {
  const { competitors, metadata } = analysis;

  const [statsOpen, setStatsOpen] = useState(true);
  const [competitorsOpen, setCompetitorsOpen] = useState(true);

  return (
    <DevCardBadge name="EngineDetailCompetitor">
    <div className={`space-y-6 ${className}`}>
      {/* Discovery Metadata */}
      <CollapsibleSection
        title="Discovery Statistics"
        isExpanded={statsOpen}
        onToggle={() => setStatsOpen((v) => !v)}
        headerColor="gray"
        forceExpanded={printMode}
      >
        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <p className="text-2xl font-bold text-zinc-950 dark:text-white">
              {metadata.totalAppsInCategory}
            </p>
            <Text className="text-xs">Total in Category</Text>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-zinc-950 dark:text-white">
              {metadata.finalCompetitorCount}
            </p>
            <Text className="text-xs">Final Competitors</Text>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-zinc-950 dark:text-white">
              {(metadata.processingTimeMs / 1000).toFixed(1)}s
            </p>
            <Text className="text-xs">Processing Time</Text>
          </div>
          <div className="text-center">
            <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
              {new Date(metadata.executedAt).toLocaleDateString()}
            </p>
            <Text className="text-xs">Executed At</Text>
          </div>
        </div>

        {/* Filter Pipeline */}
        <div className="pt-4 border-t border-zinc-950/10 dark:border-white/10">
          <Subheading level={5} className="mb-3">Filter Pipeline</Subheading>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="px-2 py-1 bg-zinc-100 dark:bg-zinc-800 rounded text-zinc-600 dark:text-zinc-400">
              {metadata.totalAppsInCategory} total
            </span>
            <span className="text-zinc-400">→</span>
            <span className="px-2 py-1 bg-zinc-100 dark:bg-zinc-800 rounded text-zinc-600 dark:text-zinc-400">
              {metadata.appsAfterSelfExclusion} after self
            </span>
            <span className="text-zinc-400">→</span>
            <span className="px-2 py-1 bg-zinc-100 dark:bg-zinc-800 rounded text-zinc-600 dark:text-zinc-400">
              {metadata.appsAfterPublisherFilter} after publisher
            </span>
            <span className="text-zinc-400">→</span>
            <span className="px-2 py-1 bg-zinc-100 dark:bg-zinc-800 rounded text-zinc-600 dark:text-zinc-400">
              {metadata.appsAfterMaintenanceFilter} after maintenance
            </span>
            <span className="text-zinc-400">→</span>
            <span className="px-2 py-1 bg-zinc-100 dark:bg-zinc-800 rounded text-zinc-600 dark:text-zinc-400">
              {metadata.appsAfterRatingFilter} after rating
            </span>
            <span className="text-zinc-400">→</span>
            <span className="px-2 py-1 bg-indigo-100 dark:bg-indigo-900/30 rounded text-indigo-700 dark:text-indigo-400 font-medium">
              {metadata.finalCompetitorCount} final
            </span>
          </div>
          {metadata.targetPublisherName && (
            <Text className="mt-2 text-xs">
              Publisher excluded: {metadata.targetPublisherName}
            </Text>
          )}
          {metadata.targetAppExcluded && (
            <Text className="mt-1 text-xs">Target app was excluded from results</Text>
          )}
        </div>
      </CollapsibleSection>

      {/* Competitors */}
      <CollapsibleSection
        title={`Discovered Competitors (${competitors.length})`}
        isExpanded={competitorsOpen}
        onToggle={() => setCompetitorsOpen((v) => !v)}
        headerColor="gray"
        forceExpanded={printMode}
      >
        <CompetitorAnalysisView competitors={competitors} />
      </CollapsibleSection>
    </div>
    </DevCardBadge>
  );
}
